/**
 * User Preferences Page
 * Allows users to customize their experience
 * Part of Phase 6E - UX Enhancements
 */

import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Switch,
  Select,
  InputNumber,
  Button,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Alert,
  ColorPicker,
  Radio,
  Tabs,
  message,
} from 'antd';
import {
  BgColorsOutlined,
  GlobalOutlined,
  DashboardOutlined,
  SettingOutlined,
  BellOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import pushNotificationService, {
  type ApprovalPushPreferences,
} from '@/services/pushNotification.service';
import { BrowserPushManager } from '@/components/notifications/BrowserPushManager';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { productionLanguages } from '@/i18n/languageRegistry';

const { Title, Text } = Typography;
const { Option } = Select;

export const UserPreferencesPage: React.FC = () => {
  const { preferences, updatePreference, updatePreferences, resetPreferences } = useUserPreferences();
  const [form] = Form.useForm();
  const [approvalPrefs, setApprovalPrefs] = useState<ApprovalPushPreferences | null>(null);

  useEffect(() => {
    form.setFieldsValue(preferences);
  }, [preferences, form]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const status = await pushNotificationService.getStatus();
        if (!mounted) return;
        setApprovalPrefs(status.preferences);
      } catch {
        if (mounted) setApprovalPrefs(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleApprovalPrefChange = async (key: keyof ApprovalPushPreferences, value: boolean) => {
    if (!approvalPrefs) return;
    const next = { ...approvalPrefs, [key]: value };
    setApprovalPrefs(next);
    try {
      await pushNotificationService.updatePreferences({ [key]: value });
    } catch {
      setApprovalPrefs(approvalPrefs);
      message.error('Failed to save approval notification preference');
    }
  };

  const handleValuesChange = (changedValues: any) => {
    const key = Object.keys(changedValues)[0];
    const value = changedValues[key];
    updatePreference(key as any, value);
  };

  const handleReset = () => {
    resetPreferences();
    form.resetFields();
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2}>⚙️ User Preferences</Title>
          <Text type="secondary">Customize your experience</Text>
        </div>

        <Alert
          message="Settings are saved automatically"
          description="Your preferences are saved as you change them and will be applied immediately."
          type="info"
          showIcon
          closable
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={preferences}
          onValuesChange={handleValuesChange}
        >
          <Tabs
            defaultActiveKey="appearance"
            items={[
              {
                key: 'appearance',
                label: (
                  <span>
                    <BgColorsOutlined /> Appearance
                  </span>
                ),
                children: (
                  <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Theme"
                            name="theme"
                            tooltip="Choose your preferred theme"
                          >
                            <Radio.Group>
                              <Radio.Button value="light">☀️ Light</Radio.Button>
                              <Radio.Button value="dark">🌙 Dark</Radio.Button>
                              <Radio.Button value="auto">🔄 Auto</Radio.Button>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Primary Color"
                            name="primaryColor"
                            tooltip="Customize the primary color"
                          >
                            <ColorPicker
                              showText
                              value={preferences.primaryColor}
                              onChange={(color) => {
                                const hex = color.toHexString();
                                updatePreference('primaryColor', hex);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Font Size"
                            name="fontSize"
                            tooltip="Adjust text size"
                          >
                            <Select>
                              <Option value="small">Small</Option>
                              <Option value="medium">Medium</Option>
                              <Option value="large">Large</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Compact Mode"
                            name="compactMode"
                            valuePropName="checked"
                            tooltip="Reduce spacing for more content"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Space>
                  </Card>
                ),
              },
              {
                key: 'regional',
                label: (
                  <span>
                    <GlobalOutlined /> Regional
                  </span>
                ),
                children: (
                  <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Interface language"
                            name="ui_language"
                            tooltip="Controls screens, navigation, and RTL/LTR. Independent of document language."
                          >
                            <LanguageSwitcher showLabel={false} />
                          </Form.Item>
                          <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 12 }}>
                            Production-supported: {productionLanguages().map((l) => l.nativeName).join(', ')}.
                            Layout direction follows the selected language.
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Document language"
                            name="document_language"
                            tooltip="Default language for quotations, POs, invoices, and print. Does not change the UI language."
                          >
                            <Select>
                              {productionLanguages().map((l) => (
                                <Option key={l.code} value={l.code}>
                                  {l.nativeName}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Time Format"
                            name="timeFormat"
                            tooltip="12-hour or 24-hour format"
                          >
                            <Radio.Group>
                              <Radio.Button value="12h">12-hour</Radio.Button>
                              <Radio.Button value="24h">24-hour</Radio.Button>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Date Format"
                            name="dateFormat"
                            tooltip="How dates are displayed"
                          >
                            <Select>
                              <Option value="YYYY-MM-DD">2026-02-04</Option>
                              <Option value="DD/MM/YYYY">04/02/2026</Option>
                              <Option value="MM/DD/YYYY">02/04/2026</Option>
                              <Option value="DD-MMM-YYYY">04-Feb-2026</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Timezone"
                            name="timezone"
                            tooltip="Your local timezone"
                          >
                            <Select showSearch>
                              <Option value="UTC">UTC</Option>
                              <Option value="Asia/Dubai">Asia/Dubai (UAE)</Option>
                              <Option value="Asia/Riyadh">Asia/Riyadh (Saudi Arabia)</Option>
                              <Option value="America/New_York">America/New York (EST)</Option>
                              <Option value="Europe/London">Europe/London (GMT)</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Space>
                  </Card>
                ),
              },
              {
                key: 'dashboard',
                label: (
                  <span>
                    <DashboardOutlined /> Dashboard
                  </span>
                ),
                children: (
                  <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Form.Item
                            label="Default Dashboard"
                            name="defaultDashboard"
                            tooltip="Dashboard to show on login"
                          >
                            <Select>
                              <Option value="overview">Overview Dashboard</Option>
                              <Option value="manufacturing">Manufacturing Dashboard</Option>
                              <Option value="sales">Growth & Revenue Dashboard</Option>
                              <Option value="inventory">Inventory Dashboard</Option>
                              <Option value="finance">Finance Dashboard</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Alert
                        message="Custom Dashboard Builder"
                        description="Drag-and-drop dashboard customization coming soon!"
                        type="info"
                        showIcon
                      />
                    </Space>
                  </Card>
                ),
              },
              {
                key: 'navigation',
                label: (
                  <span>
                    <SettingOutlined /> Navigation
                  </span>
                ),
                children: (
                  <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <Form.Item
                            label="Sidebar Collapsed"
                            name="sidebarCollapsed"
                            valuePropName="checked"
                            tooltip="Start with sidebar collapsed"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label="Show Breadcrumbs"
                            name="showBreadcrumbs"
                            valuePropName="checked"
                            tooltip="Show navigation breadcrumbs"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label="Show Quick Actions"
                            name="showQuickActions"
                            valuePropName="checked"
                            tooltip="Show floating action button"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Items Per Page"
                            name="itemsPerPage"
                            tooltip="Default number of items in lists"
                          >
                            <Select>
                              <Option value={10}>10 items</Option>
                              <Option value={25}>25 items</Option>
                              <Option value={50}>50 items</Option>
                              <Option value={100}>100 items</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Space>
                  </Card>
                ),
              },
              {
                key: 'features',
                label: (
                  <span>
                    <BellOutlined /> Features
                  </span>
                ),
                children: (
                  <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <Form.Item
                            label="Notifications"
                            name="enableNotifications"
                            valuePropName="checked"
                            tooltip="Enable browser notifications"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label="Sounds"
                            name="enableSounds"
                            valuePropName="checked"
                            tooltip="Play notification sounds"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label="Keyboard Shortcuts"
                            name="enableKeyboardShortcuts"
                            valuePropName="checked"
                            tooltip="Enable keyboard shortcuts"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Form.Item
                            label="Help Tooltips"
                            name="showHelpTooltips"
                            valuePropName="checked"
                            tooltip="Show helpful tooltips"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Developer Mode"
                            name="developerMode"
                            valuePropName="checked"
                            tooltip="Enable developer features"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Divider orientation="left">Approval notifications</Divider>
                      <BrowserPushManager
                        onPreferencesChange={(p) => {
                          if (p) setApprovalPrefs(p);
                        }}
                      />
                      {approvalPrefs ? (
                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                          <Col span={12}>
                            <Space>
                              <Switch
                                checked={approvalPrefs.notify_assigned}
                                onChange={(v) => void handleApprovalPrefChange('notify_assigned', v)}
                              />
                              <Text>Assigned / resubmitted</Text>
                            </Space>
                          </Col>
                          <Col span={12}>
                            <Space>
                              <Switch
                                checked={approvalPrefs.notify_overdue}
                                onChange={(v) => void handleApprovalPrefChange('notify_overdue', v)}
                              />
                              <Text>Overdue reminders</Text>
                            </Space>
                          </Col>
                          <Col span={12}>
                            <Space>
                              <Switch
                                checked={approvalPrefs.notify_escalation}
                                onChange={(v) => void handleApprovalPrefChange('notify_escalation', v)}
                              />
                              <Text>Escalations</Text>
                            </Space>
                          </Col>
                          <Col span={12}>
                            <Space>
                              <Switch
                                checked={approvalPrefs.notify_rejection}
                                onChange={(v) => void handleApprovalPrefChange('notify_rejection', v)}
                              />
                              <Text>Rejections / returns</Text>
                            </Space>
                          </Col>
                              <Col span={12}>
                                <Space>
                                  <Switch
                                    checked={approvalPrefs.notify_final}
                                    onChange={(v) => void handleApprovalPrefChange('notify_final', v)}
                                  />
                                  <Text>Final approvals</Text>
                                </Space>
                              </Col>
                            </Row>
                          ) : null}
                    </Space>
                  </Card>
                ),
              },
              {
                key: 'keyboard',
                label: (
                  <span>
                    <KeyOutlined /> Shortcuts
                  </span>
                ),
                children: (
                  <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Alert
                        message="Keyboard Shortcuts"
                        description="Press ? to view all available keyboard shortcuts"
                        type="info"
                        showIcon
                      />

                      <div>
                        <Title level={5}>Global Shortcuts</Title>
                        <Space direction="vertical" size="small">
                          <Text><code>Ctrl+K</code> / <code>Cmd+K</code> - Global Search</Text>
                          <Text><code>?</code> - Show Shortcuts Help</Text>
                          <Text><code>Esc</code> - Close Dialogs</Text>
                          <Text><code>Alt+N</code> - New Document (contextual)</Text>
                        </Space>
                      </div>

                      <Divider />

                      <div>
                        <Title level={5}>Navigation</Title>
                        <Space direction="vertical" size="small">
                          <Text><code>G then D</code> - Go to Dashboard</Text>
                          <Text><code>G then S</code> - Go to Sales</Text>
                          <Text><code>G then M</code> - Go to Manufacturing</Text>
                          <Text><code>G then I</code> - Go to Inventory</Text>
                        </Space>
                      </div>
                    </Space>
                  </Card>
                ),
              },
            ]}
          />
        </Form>

        <Card>
          <Space>
            <Button type="primary" danger onClick={handleReset}>
              Reset to Defaults
            </Button>
            <Text type="secondary">All preferences will be reset to factory defaults</Text>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default UserPreferencesPage;
